// File contains all our helper functions for SS.

declare var Shopify: { country: string  }

import { ConvertedProductResponse, ConvertedPropertyNode, Option, Payload, Result, SearchSpringCarouselResponse, SearchspringConfig, SearchspringHeaders, SearchSpringProductResponse, SearchSpringSuggestResult, SuggestionsResponse, TrendingSuggestions } from "../search-spring/types/types";
import { getProductCurrencyData } from "./getProductPrice"


  const getSiteId = (type?: string | null) => {
    // global variable defined in searchspring init liquid file
    if (type == 'collections') {
      return CD_STATE.searchspring.collectionId
    }
    if (type == 'pages') {
      return CD_STATE.searchspring.pageId
    }
    if (type == 'blogs') {
      return CD_STATE.searchspring.blogId
    }
    if (type == null) {
      return CD_STATE.searchspring.siteId
    }
  }

  const getPageLoadId = async (key = 'ss-page-load-id'): Promise<string> => {
    const ssPageLoadId = sessionStorage.getItem(key)

    return ssPageLoadId || ''
  }

  function getCookie(name: string): string | null {
    const pair = document.cookie.match(new RegExp(name + '=([^;]+)'));
    return pair ? pair[1] : null;
  }

  export const getSearchspringUrl = async (
    pathname: 'search' | 'autocomplete' = 'search',
    params: URLSearchParams = new URLSearchParams(),
    siteIdType?: string | null
  ): Promise<string> => {
    params.append('resultsFormat', 'native')
    params.append('redirectResponse', 'full')
    params.append('domain', window.location.href)
    
    const urlParams = `&${params.toString()}`
    return `https://${getSiteId(siteIdType)}.a.searchspring.io/api/search/${pathname}.json?siteId=${getSiteId(siteIdType)}${urlParams}`
  }

  export const getSuggestionsUrl = async (
    params: URLSearchParams = new URLSearchParams()
  ): Promise<string> => {
    const siteIdType = null
    const pathname = 'suggest'
    params.append('resultsFormat', 'native')
    params.append('redirectResponse', 'full')
    params.append('domain', window.location.href)

    params = await generateTrackingCookieParams(params, 'suggestion')

    const urlParams = `&${params.toString()}`
    return `https://${getSiteId(siteIdType)}.a.searchspring.io/api/${pathname}/query?siteId=${getSiteId(siteIdType)}${urlParams}`
  }

  export const getSearchspringId = async (type: 'user' | 'session'): Promise<string> => {
    if (!type || (type !== 'session' && type !== 'user')) {
      console.error("Missing type at getSearchspringId function")
    }

    const storageItemName = type === 'user' ? 'ssUserId' : 'ssSessionIdNamespace';
    const searchspringId = getCookie(storageItemName);

    return searchspringId || '';
  }

  export const getSuggestions = async (inputValue: string): Promise<SuggestionsResponse | null>  => {
    const siteId = getSiteId()
    const url = `https://${siteId}.a.searchspring.io/api/suggest/query?siteId=${siteId}&query=${inputValue}&language=en&suggestionCount=4&&productCount=5`
    try {
      const response = await fetch(url)
      const suggestions = await response.json()
      return suggestions
    } catch (error) {
      console.error('error retrieving suggestions:', error)
      return null
    }
  }

  export const generateTrackingCookieParams = async (params: URLSearchParams, type: string) => {
    const ssViewedProducts = getCookie('ssViewedProducts')
    const ssShopperId = getCookie('ssShopperId')
    const ssCartProducts = getCookie('ssCartProducts')
    const ssSessionIdNamespace = await getSearchspringId('session');
    const ssPageLoadId = await getPageLoadId('ss-page-load-id')
    const ssUserId = await getSearchspringId('user')

    if (ssUserId) {
      params.append('userId', ssUserId)
    }

    if (ssViewedProducts) {
      params.append('lastViewed', ssViewedProducts)
    }

    if (ssShopperId) {
      params.append('shopper', ssShopperId)
    }

    if (ssCartProducts) {
      params.append('cart', ssCartProducts)
    }

    if (ssSessionIdNamespace) {
      params.append('sessionId', ssSessionIdNamespace)
    }

    if (ssPageLoadId) {
      params.append('pageLoadId', ssPageLoadId)
    }

    return params
  }

  export const getTrendingSuggestions = async (): Promise<TrendingSuggestions | null> => {
    const siteId = getSiteId()
    let params = new URLSearchParams()

    params.append('siteId', siteId)
    params.append('limit', "5")

    params = await generateTrackingCookieParams(params, 'trending')


    const url = `https://${siteId}.a.searchspring.io/api/suggest/trending?${params.toString()}`
    try {
      const response = await fetch(url)
      const trendingSuggestions = await response.json()
      return trendingSuggestions
    } catch (error) {
      console.error('error retrieving suggestions:', error)
      return null
    }
  }

  const getActiveFilters = async (
    params: URLSearchParams,
    filters: { fieldValue: string; optionValues: string[] }[]
    ): Promise<URLSearchParams> => {

    for (const filter of filters) {
      if (!filter || !filter.optionValues || !filter.optionValues.length || !filter.fieldValue) {
        continue;
      }
      const key = filter.fieldValue;
      const values = filter.optionValues;

      if (key === 'sort' && values.length) {
        const [sortKey, sortValue] = values[0].split('=')
        params.append(`sort.${sortKey}`, sortValue)
      } else {
        for (const value of values) {
          params.append(`filter.${key}`, value);
        }
      }
    }
    return params;
  }

    /**
   * Standardised method to get params for both search and collections
   * To do: get a type for payload -- see searchspring UI class
   * @param { URLSearchParams } params
   * @param { any } payload
   * @returns { URLSearchParams }
   */
    const getParams = async (
      params: URLSearchParams, 
      payload: any | null = null
    ): Promise<URLSearchParams> => {
      if (!payload) {
        return params;
      }

      const { resultsPerPage, activeFilters, activePage, tag, originalQuery, page } = payload;
  
      if (resultsPerPage) {
        params.append('resultsPerPage', resultsPerPage);
      }
  
      if (activeFilters) {
        params = await getActiveFilters(params, activeFilters);
      }
      if (activePage) {
        params.append('page', activePage);
      }
      if (originalQuery) {
        params.append('oq', originalQuery);
      }
      if (tag) {
        params.append('tag', tag);
      }
      if (page) {
        params.append('page', page);
      }

      params = await generateTrackingCookieParams(params, 'search')

      return params;
    }

  const getSearchspringHeaders = async(): Promise<SearchspringHeaders> => {
    const ssUserId = await getSearchspringId('user');
    const ssSessionIdNamespace = await getSearchspringId('session');
    const ssPageLoadId = await getPageLoadId('ss-page-load-id')
    return {
      Accept: 'application/json',
      'searchspring-user-id': ssUserId,
      'searchspring-session-id': ssSessionIdNamespace,
      'searchspring-page-load-id': ssPageLoadId,
    };
  }

  const getSearchspringConfig = async (): Promise<SearchspringConfig> => {
    return {
      method: 'POST',
      headers: await getSearchspringHeaders(),
    };
  }

  const getNormalizedResponse = async (
    url: string,
    config: SearchspringConfig,
    searchQuery: string,
    searchType: 'getCollectionByHandle' | 'autocomplete' | 'search' = 'search'
  ): Promise<SearchSpringProductResponse | SearchSpringSuggestResult> => {
    const searchspringResponse = await fetch(url, config)
      .then((res) => res.json())
      .then((data) => {
        if (data?.sorting?.options?.length) {
          data.sorting.options = data.sorting.options.map((x: Option) => {
            return {
              ...x,
              active: x.active === null || x.active === undefined ? 0 : x.active,
            };
          });
        }
        return data;
      })
      .catch((err) => {
        console.warn('Error fetching for', searchType, 'searching ', searchQuery, 'err: ', err);
        return [];
      });

    return searchspringResponse;
  }

  export const convertSearchSpringProductPricingToMarketPrice = async (searchSpringData: SearchSpringProductResponse): Promise<SearchSpringProductResponse> => {
    const market: string = Shopify.country
    const productIds: string[] = []
    searchSpringData.results.forEach(product => {
      productIds.push(`gid://shopify/Product/${product.uid}`)
    })

    // Getting correct prices for each product from storefront
    const convertedProductPrices: ConvertedProductResponse = await getProductCurrencyData({productIds: productIds, country: market})
    const validPrices: ConvertedPropertyNode[] = convertedProductPrices.nodes.filter(price => price);

    // Need to then match the ID with the data returned
    // transform the price to the new currency 
    searchSpringData.results = searchSpringData.results.filter(product => {
      const updatedPrice = validPrices.find(price => {
        const idParts = price.id.split('/');
        return idParts[idParts.length - 1] === product.uid;
      });
      
      if (updatedPrice) {
        product.price = updatedPrice.variants.nodes[0].price.amount;
        return true;
      } else {
        console.warn(`Please enable "CD | Storefront API" Checkbox in Sales channels for ${product.uid}`);
        return false;
      }
    });

    // return the transformed data
    return searchSpringData
  }

  export const convertSearchSpringCarouselProductPricingToMarketPrice = async (searchSpringData: SearchSpringCarouselResponse): Promise<SearchSpringCarouselResponse> => {
    const market: string = Shopify.country
    const productIds: string[] = []
    searchSpringData.results.forEach(product => {
      productIds.push(`gid://shopify/Product/${product.id}`)
    })

    // Getting correct prices for each product from storefront
    const convertedProductPrices: ConvertedProductResponse = await getProductCurrencyData({productIds: productIds, country: market})
    
    // Need to then match the ID with the data returned
    // transform the price to the new currency 
    searchSpringData.results.forEach(product => {
      convertedProductPrices.nodes.forEach(updatedPrice => {
        const idParts = updatedPrice.id.split('/')
        const id = idParts[idParts.length -1]
        if (product.id === id) {
          product.mappings.core.price = parseInt(updatedPrice.variants.nodes[0].price.amount)
        }
      })
    })

    // return the transformed data
    return searchSpringData
  }

  type StandardSearchParams = {
    searchQuery?: string
    searchType?: 'autocomplete' | 'search' | 'collection'
    siteIdType?: string | null
    payload?: Payload | null
    initPageParam?: number | null
  };

  export const standardSearch = async ({
    searchQuery = '',
    searchType = 'search',
    siteIdType = null,
    payload = null,
    initPageParam = null
  }: StandardSearchParams): Promise<SearchSpringProductResponse | unknown> => {
    /*
      If there is an initPageParam, we will need to do a different search then the one here as 
      the pages are not paginated, they are stacked on top of eachother. So we will actually need to
      do initPageParam x number of searches. Then join the response results together and return it.
    */
    if (initPageParam) {
      const responseData = await standardSearchWithExistingParams({initPageParam, payload, searchQuery, searchType})
      return responseData
    } 
    
    let params = new URLSearchParams()
    if (initPageParam && payload && payload.page && payload.resultsPerPage) {
      payload.resultsPerPage = payload.page * payload.resultsPerPage
    }

    if (searchType === 'collection') {
      params.append('bgfilter.collection_handle', searchQuery)
      params.append('bgfilter.ss_collection_exclude', '0')
      // we reuse this param later for creating the searchspring URL
      // there is no collection path, we must use search instead
      searchType = 'search'
    } else {
      params.append('q', searchQuery)
      params.append('bgfilter.ss_search_exclude', '0')
    }

    params = await getParams(params, payload)

    // do not set url params for autocomplete
    // if (searchType === 'search') {
    //   setUrl(params)
    // }

    try {
      const url = await getSearchspringUrl(searchType, params, siteIdType)
      const config = await getSearchspringConfig()
      const normalizedResponse = getNormalizedResponse(url, config, searchQuery, searchType)
      return normalizedResponse
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to perform standard search: ${error.message}`);
      } else {
        throw new Error('Failed to perform standard search: Unknown error');
      }
    }
  }

export const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

export const isSearchSpringProductResponse = (value: unknown): value is SearchSpringProductResponse => {
  return (value as SearchSpringProductResponse).results !== undefined
}

export const isSearchSpringCarouselProductResponse = (value: unknown): value is SearchSpringCarouselResponse[] => {
  return (
    Array.isArray(value) &&
    value.every(item => typeof item === 'object' && item !== null && 'results' in item)
  )
}

type StandardExistingSearchParams = {
  initPageParam: number
  payload: Payload | null
  searchQuery: string
  searchType?: StandardSearchParams['searchType']
}

const standardSearchWithExistingParams = async ({
  initPageParam, payload, searchQuery, searchType}: StandardExistingSearchParams): Promise<SearchSpringProductResponse | unknown> => {
  if (initPageParam && payload && searchQuery) {
    const products: Result[] = []
    const promises: Promise<SearchSpringProductResponse | unknown>[] = []
    
    for (let i = 0; i < initPageParam; i++) {
      const pagedPayload: Payload = {
        ...payload,
        page: i + 1,
      }

      promises.push(standardSearch({ payload: pagedPayload, searchQuery, searchType }))
    }
 
    const results: PromiseSettledResult<SearchSpringProductResponse | unknown>[] = await Promise.allSettled(promises)
    let finalResponse: SearchSpringProductResponse | undefined

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && isSearchSpringProductResponse(result.value)) {
        products.push(...result.value.results)

        if (index === results.length - 1) {
          finalResponse = result.value
        }
      } else {
        throw new Error(`Failed request at index: ${index}, result: ${result}` )
      }
    })

    // our final iteration updates with all the information we need, except products which we will add now.
    if (finalResponse) {
      finalResponse.results = products
      return finalResponse
    }
    
  }
  throw new Error('Failed to perform standard search: Unknown error:')
}

export const getCarouselItems = async (payload: Payload): Promise<SearchSpringCarouselResponse[]> => {
  const siteId = getSiteId()
  const params = new URLSearchParams()

  if (payload.productId) {
    params.append('product', payload.productId)
  }
  if (payload.lastViewed) {
    params.append('lastViewed', payload.lastViewed)
  }
  if (payload.tags) {
    params.append('tags', payload.tags)
  }
  if (payload.limits) {
    params.append('limits', payload.limits)
  }
  if (payload.cart) {
    params.append('cart', payload.cart)
  }
  if (payload.shopper) {
    params.append('shopper', payload.shopper)
  }

  const endpoint = `https://${siteId}.a.searchspring.io/boost/${siteId}/recommend?${params}`
  try {
    const response = await fetch(endpoint);
    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }
    const result: SearchSpringCarouselResponse[] = await response.json();
    return result;
  } catch (err) {
    console.error('Could not get recommendations, returning blank results object', err);
    return {
      profile: {
        tag: payload.tags || ''
      },
      results: []
    };
  }
} 

export const getInstantSearchSuggestions = async (instantSearchQuery: string, suggestionCount: number): Promise<SearchSpringProductResponse | unknown> => {
  let params = new URLSearchParams()
  params.append('q', instantSearchQuery)
  params.append('suggestionCount', suggestionCount.toString())
  params.append('language', 'en')
  
  try {
    const url = await getSuggestionsUrl(params)
    const config = await getSearchspringConfig()
    const normalizedResponse: any = await getNormalizedResponse(url, config, instantSearchQuery, 'search')
    const query = normalizedResponse?.suggested?.text || normalizedResponse["corrected-query"] || instantSearchQuery
    const payload = { resultsPerPage: suggestionCount }

    return await standardSearch({ payload, searchQuery: query, searchType: 'autocomplete' })
  } catch (error) {
    const errorMessage = error instanceof Error 
      ? `Failed to get suggestions: ${error.message}`
      : 'Failed to get suggestions'
    throw new Error(errorMessage)
  }
}

export const elInViewPort = (el: any, partiallyVisible = false) => {
  const { top, left, bottom, right } = el.getBoundingClientRect()
  const { innerHeight, innerWidth } = window

  return partiallyVisible
    ? ((top > 0 && top < innerHeight) || (bottom > 0 && bottom < innerHeight)) && ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth))
    : top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth
}